import React, { useEffect, useMemo, useState } from 'react';
import { Network } from '@capacitor/network';

const DISCONNECT_DEBOUNCE_MS = 1000;

interface NetworkContextInterface {
  isConnected: boolean;
}

const NetworkContext = React.createContext<NetworkContextInterface | null>(null);

const NetworkProvider: React.FC = (props) => {
  const [isConnected, setIsConnected] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;
    let disconnectTimeout: NodeJS.Timeout | null = null;

    // Initial status check
    Network.getStatus().then((status) => {
      if (mounted) setIsConnected(status.connected);
    }).catch(() => {
      // Plugin unavailable (e.g., web browser during development) — default to connected
    });

    const listenerPromise = Network.addListener('networkStatusChange', (status) => {
      if (!mounted) return;

      if (status.connected) {
        // RECONNECTION: immediate, cancel any pending disconnect
        if (disconnectTimeout) {
          clearTimeout(disconnectTimeout);
          disconnectTimeout = null;
        }
        setIsConnected(true);
      } else {
        // DISCONNECTION: debounce 1s, then re-query OS to confirm.
        // Android onLost() fires false disconnects during WiFi-to-cellular
        // handoffs (200-800ms). The debounce lets the handoff complete,
        // then Network.getStatus() reflects the real state.
        if (!disconnectTimeout) {
          disconnectTimeout = setTimeout(async () => {
            disconnectTimeout = null;
            const current = await Network.getStatus();
            if (!current.connected && mounted) {
              setIsConnected(false);
            }
          }, DISCONNECT_DEBOUNCE_MS);
        }
      }
    });

    return () => {
      mounted = false;
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      listenerPromise?.then((h) => h.remove()).catch(() => {});
      if (disconnectTimeout) clearTimeout(disconnectTimeout);
    };
  }, []);

  const value = useMemo(() => ({ isConnected }), [isConnected]);

  return (
    <NetworkContext.Provider value={value} {...props} />
  );
};

const useNetwork = (): NetworkContextInterface => {
  const context = React.useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};

export { useNetwork };
export default NetworkProvider;
